import { useState } from 'react';
import {
    Briefcase,
    Trophy,
    ThumbsUp,
    Send,
    TrendingUp,
    Building,
    Mail,
    Clock,
    Filter
} from 'lucide-react';

const Main = () => {
    const [selectedFilter, setSelectedFilter] = useState('all');

    const jobChanges = [
        {
            person: {
                name: "Sarah Chen",
                newRole: "Head of IP Operations",
                company: "TechVision AI",
                previousRole: "Senior IP Counsel",
                previousCompany: "DataCorp",
                changeDate: "2024-01-05",
                profileStrength: 92
            },
            companyResearch: {
                revenue: "£50M+",
                lastFunding: "Series C - £75M",
                growthRate: "156%",
                aiInsights: [
                    "Expanding UK IP portfolio",
                    "Recent AI patent applications",
                    "Planning European expansion"
                ],
                matchScore: 95
            },
            campaignStatus: {
                stage: "Ready",
                template: "AI-Tech Growth",
                priority: "High",
                suggestedApproach: "Personalized IP Strategy"
            }
        },
        {
            person: {
                name: "James Wilson",
                newRole: "IP Director",
                company: "InnovateAI Ltd",
                previousRole: "Patent Manager",
                previousCompany: "Tech Solutions",
                changeDate: "2024-01-08",
                profileStrength: 88
            },
            companyResearch: {
                revenue: "£30M+",
                lastFunding: "Series B - £40M",
                growthRate: "122%",
                aiInsights: [
                    "New AI product launch in Q1",
                    "Growing trademark portfolio",
                    "UK market leader in sector"
                ],
                matchScore: 87
            },
            campaignStatus: {
                stage: "Draft",
                template: "Innovation Growth",
                priority: "Medium",
                suggestedApproach: "Portfolio Review"
            }
        }
    ];

    // AI-generated congratulatory templates
    const campaignTemplates = {
        "AI-Tech Growth": {
            subject: "Congratulations on Your New Role at {{company}}",
            body: `Dear {{name}},

Congratulations on your new position as {{role}} at {{company}}! Your move comes at an exciting time, given {{company}}'s recent expansion in AI technology and IP development.

I noticed {{company}}'s impressive growth ({{growthRate}} YoY) and recent {{funding}} funding round. Given your focus on IP operations and {{company}}'s expanding portfolio, I'd love to discuss how we can support your IP strategy, particularly around:

1. AI patent portfolio development
2. International trademark protection
3. IP portfolio optimization

Would you be open to a brief conversation about how we can support your IP objectives in your new role?

Best regards,
[Your name]`
        }
    };

    return (
        <div className="space-y-6">
            {/* Overview Card */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        <span className='font-bold'>Job Change Campaign Manager</span>
                    </div>
                    <div>
                        AI-powered congratulations campaigns for qualified leads
                    </div>
                </div>
                <div>
                    <div className="flex gap-4 mb-6 items-center mt-3 justify-end">
                        <select value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)} className="w-[200px] focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                            <option value="all">All Changes</option>
                            <option value="high">High Priority</option>
                            <option value="pending">Pending Research</option>
                        </select>
                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                            <Filter className="w-4 h-4" />
                            Apply Filters
                        </button>
                    </div>

                    <div className="space-y-6">
                        {jobChanges.map((change, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                {/* Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-medium flex items-center gap-2">
                                            {change.person.name}
                                            <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm
                                                ${change.companyResearch.matchScore >= 90 ? "bg-[#d1fae5] text-[#065f46]" :
                                                    "bg-blue-100 text-blue-800"}
                                            `}>
                                                {change.companyResearch.matchScore}% Match
                                            </div>
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            {change.person.newRole} at {change.person.company}
                                        </p>
                                    </div>
                                    <div
                                        className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm
                                     ${change.campaignStatus.priority === 'High' ? "bg-[#d1fae5] text-[#065f46]" :
                                                "bg-blue-100 text-blue-800"}
                                 `}>
                                        {change.campaignStatus.priority} Priority
                                    </div>
                                </div>

                                {/* Company Research */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <span className="text-sm text-gray-500">Revenue</span>
                                        <div className="font-medium">{change.companyResearch.revenue}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Last Funding</span>
                                        <div className="font-medium">{change.companyResearch.lastFunding}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Growth</span>
                                        <div className="font-medium">{change.companyResearch.growthRate}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Changed</span>
                                        <div className="font-medium">{change.person.changeDate}</div>
                                    </div>
                                </div>

                                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                                    <h4 className="text-sm font-medium mb-2">AI Research Insights</h4>
                                    <ul className="space-y-1">
                                        {change.companyResearch.aiInsights.map((insight, i) => (
                                            <li key={i} className="text-sm flex items-center gap-2">
                                                <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                                {insight}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Campaign Actions */}
                                <div className="flex justify-between items-center">
                                    <div className="text-sm">
                                        <span className="text-gray-500">Suggested Approach: </span>
                                        {change.campaignStatus.suggestedApproach}
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            <Mail className="w-4 h-4" />
                                            Preview Email
                                        </button>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            <Send className="w-4 h-4" />
                                            Start Campaign
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Campaign Analytics */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        <span className='font-bold'>Campaign Performance</span>
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg">
                            <div className="text-2xl font-bold mb-1">87%</div>
                            <div className="text-sm text-gray-500">Response Rate</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <div className="text-2xl font-bold mb-1">28</div>
                            <div className="text-sm text-gray-500">Active Campaigns</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <div className="text-2xl font-bold mb-1">12</div>
                            <div className="text-sm text-gray-500">Meetings Booked</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main